/**
 * Platform Pricing — single pricing config (one pack model).
 * Edits apply immediately to every org via runtime resolution.
 */
import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CurrencyInr, FloppyDisk, Users, FilesIcon, Database, HardDrives } from "@phosphor-icons/react";

const blank = {
    monthly_price: 499, currency: "INR",
    users_per_pack: 5, forms_per_pack: 15,
    records_per_pack: 10000, storage_gb_per_pack: 1.0,
};

export default function PlatformPricingPage() {
    const [pricing, setPricing] = useState(blank);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try { const { data } = await api.get("/platform/pricing"); setPricing(data); }
        catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/platform/pricing", {
                monthly_price: Number(pricing.monthly_price),
                currency: pricing.currency,
                users_per_pack: Number(pricing.users_per_pack),
                forms_per_pack: Number(pricing.forms_per_pack),
                records_per_pack: Number(pricing.records_per_pack),
                storage_gb_per_pack: Number(pricing.storage_gb_per_pack),
            });
            toast.success("Pricing updated — applies to every org immediately");
            await load();
        } catch (e) { toast.error(extractError(e)); }
        finally { setSaving(false); }
    };

    const card = (icon, label, key, suffix, step = 1) => {
        const Icon = icon;
        return (
            <div className="brut-section p-5" data-testid={`pricing-card-${key}`}>
                <div className="flex items-center gap-2 mb-3">
                    <Icon size={18} weight="duotone" className="text-primary" />
                    <span className="label-overline">{label}</span>
                </div>
                <div className="flex items-end gap-2">
                    <Input
                        type="number" step={step}
                        value={pricing[key]}
                        onChange={(e) => setPricing({ ...pricing, [key]: e.target.value })}
                        className="rounded-sm h-12 text-xl font-bold tabular-nums"
                        data-testid={`pricing-input-${key}`}
                    />
                    <span className="text-sm text-muted-foreground pb-3">{suffix}</span>
                </div>
            </div>
        );
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Platform"
                title="Pricing"
                description="Define the single standard pack. Annual billing (monthly × 12). Changes apply to every organization immediately."
                actions={
                    <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-pricing-btn">
                        <FloppyDisk size={16} className="mr-2" /> {saving ? "Saving…" : "Save pricing"}
                    </Button>
                }
                testid="pricing-header"
            />

            <div className="brut-section p-8 mb-6" data-testid="pricing-price-row">
                <div className="grid sm:grid-cols-2 gap-6 items-end">
                    <div>
                        <Label className="label-overline">Monthly price (per pack)</Label>
                        <div className="flex items-end gap-2 mt-2">
                            <div className="text-3xl font-bold pb-1">{pricing.currency === "INR" ? "₹" : pricing.currency}</div>
                            <Input
                                type="number" step="1" value={pricing.monthly_price}
                                onChange={(e) => setPricing({ ...pricing, monthly_price: e.target.value })}
                                className="rounded-sm h-14 text-3xl font-bold tabular-nums w-full"
                                data-testid="pricing-monthly-input"
                            />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 font-mono">Annual = monthly × 12 · No taxes added</p>
                    </div>
                    <div>
                        <Label className="label-overline">Currency code</Label>
                        <Input
                            value={pricing.currency}
                            onChange={(e) => setPricing({ ...pricing, currency: e.target.value.toUpperCase().slice(0, 8) })}
                            className="mt-2 rounded-sm font-mono h-14"
                            data-testid="pricing-currency-input"
                        />
                    </div>
                </div>
                {pricing.yearly_price != null && (
                    <div className="mt-6 pt-6 border-t border-border" data-testid="pricing-summary">
                        <div className="label-overline">Total / year payable</div>
                        <div className="text-2xl font-bold mt-1 text-primary tabular-nums">₹{Number(pricing.yearly_price || pricing.monthly_price * 12).toLocaleString("en-IN")}</div>
                    </div>
                )}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {card(Users, "Users per pack", "users_per_pack", "users", 1)}
                {card(FilesIcon, "Forms per pack", "forms_per_pack", "forms", 1)}
                {card(Database, "Records per pack", "records_per_pack", "records", 100)}
                {card(HardDrives, "Storage per pack", "storage_gb_per_pack", "GB", 0.5)}
            </div>

            <div className="mt-8 text-xs text-muted-foreground" data-testid="pricing-note">
                Changes are picked up at the next billing snapshot — admins and users see updated limits on their next billing-page load.
            </div>
        </PageContainer>
    );
}
